import { BaseApiClient } from './base.client';

/**
 * Attractions (Bridgify) API client.
 *
 * Domain types are declared inline and re-exported from the package barrel,
 * matching every other client in this package — there is deliberately no
 * separate `types/` file.
 *
 * These shapes mirror what `attractions-service` returns, which is a NORMALISED
 * view of Bridgify rather than its wire format. In particular the service never
 * sends `merchant_price` / `inventory_supplier` to the browser, so they do not
 * appear here at all.
 */

/* ──────────────────────────────── catalogue ───────────────────────────── */

/** BSN business-hours · CLD calendar · TSL date+time · EVT fixed event. */
export type AttractionAvailabilityType = 'BSN' | 'CLD' | 'TSL' | 'EVT';

export interface AttractionGeo {
  latitude?: number;
  longitude?: number;
}

export interface AttractionRecord {
  uuid: string;
  title: string;
  description: string;
  mainPhotoUrl?: string | null;
  photos: string[];
  currency: string;
  /** "From" price. Nullable — many Bridgify fields can be empty. */
  price: number | null;
  isFree: boolean;
  rating: number | null;
  numberOfReviews: number | null;
  duration: string | null;
  durationLabel: string | null;
  city?: string;
  country?: string;
  address?: string;
  geo?: AttractionGeo;
  categories: string[];
  availabilityType: AttractionAvailabilityType | null;
  isAccessible: boolean;
  hotelPickup: boolean;
  importantInformation: string | null;
  includedList: string[];
  excludedList: string[];
  cancellationPolicy: string | null;
  slug: string;
  citySlug?: string;
  countrySlug?: string;
}

export interface AttractionListResponse {
  message: string;
  data: AttractionRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SearchAttractionsParams {
  search?: string;
  city?: string;
  citySlug?: string;
  countrySlug?: string;
  category?: string;
  availabilityType?: AttractionAvailabilityType;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  accessibleOnly?: boolean;
  hotelPickupOnly?: boolean;
  sortBy?: 'recommended' | 'price' | 'rating' | 'title';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface AttractionCityRecord {
  city: string;
  citySlug: string;
  country?: string;
  countrySlug?: string;
  attractionCount: number;
}

/**
 * Availability PREVIEW. `authoritative: false` is not decoration — the dates
 * offered inside the cart come from a live supplier check and can disagree, so
 * a customer must never commit from this calendar.
 */
export interface AttractionAvailability {
  dates: Record<string, string[]>;
  availabilityType: AttractionAvailabilityType | null;
  authoritative: false;
  hasAvailabilityData: boolean;
}

/* ─────────────────────────── the step engine ──────────────────────────── */

export type AttractionStepKind =
  | 'options'
  | 'pickups'
  | 'shipment-method'
  | 'languages'
  | 'dates'
  | 'timeslots'
  | 'seats-map'
  | 'tickets'
  | 'optional-extras'
  | 'customer-info';

export type AttractionFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'select'
  | 'date'
  | 'time'
  | 'number'
  | 'checkbox'
  | 'textarea';

export interface AttractionFieldOption {
  value: string;
  label: string;
}

export interface AttractionFieldDescriptor {
  name: string;
  label: string;
  type: AttractionFieldType;
  required: boolean;
  options?: AttractionFieldOption[];
  min?: number;
  max?: number;
  pattern?: string;
  placeholder?: string;
  /** Groups repeated per-traveller fields, e.g. `adult_1`. */
  group?: string;
  groupLabel?: string;
  /** Passport / date-of-birth style. Drives the privacy copy. */
  sensitive?: boolean;
}

export type AttractionPickupShape = 'list' | 'list-with-times' | 'choices' | 'rect' | 'multirect';

export interface AttractionPickupPoint {
  id: string;
  name: string;
  description?: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Pre-sorted by the service — Bridgify returns these out of order. */
  times?: string[];
}

export interface AttractionPickupRect {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface AttractionTicketType {
  productId: string;
  name: string;
  holderType: string | null;
  /**
   * NULL is meaningful and common: some suppliers do not price until
   * quantities are submitted. Render "Select tickets to see your price".
   */
  retailPrice: number | null;
  currency: string | null;
  /** Explicit allowed quantities, possibly NON-contiguous. Build the picker
   *  from this list — never a free-typed number or a plain +/− to a max. */
  availableSellingQuantities: number[];
  type: string | null;
}

export interface AttractionTicketRestrictions {
  overallMaxTickets: number | null;
  maxTicketTypes: number | null;
  /** Child/youth/infant quantities are blocked until an adult is selected. */
  adultRequired: boolean;
  minimumRequired: Record<string, number>;
  maxTickets: Record<string, number>;
  /** Price depends on quantity — a disclaimer is mandatory. */
  flexiblePrice: boolean;
}

export interface AttractionOptionalExtraValue {
  valueId: string;
  valueName: string;
  valuePrice: number | null;
}

export interface AttractionOptionalExtra {
  id: string;
  title: string;
  values: AttractionOptionalExtraValue[];
}

export interface AttractionProductOption {
  optionId: string;
  title: string;
  description: string | null;
  retailPrice: number | null;
  currency: string | null;
  flexiblePrice: boolean;
}

export interface AttractionShipmentMethod {
  shipmentId: string;
  shipmentName: string;
  /** Any non-zero fee must appear in the breakdown before payment. */
  price: number | null;
  areaId: string | null;
}

export interface AttractionStepPayload {
  availableDates?: string[];
  /** May be `10:30` OR a range `08:30 - 10:30`. Echo back verbatim. */
  availableTimeslots?: string[];
  availableLanguages?: AttractionFieldOption[];
  pickupShape?: AttractionPickupShape;
  pickupPoints?: AttractionPickupPoint[];
  pickupRects?: AttractionPickupRect[];
  seatsMapUrl?: string | null;
  ticketTypes?: AttractionTicketType[];
  restrictions?: AttractionTicketRestrictions;
  optionalExtras?: AttractionOptionalExtra[];
  productOptions?: AttractionProductOption[];
  shipmentMethods?: AttractionShipmentMethod[];
  /** customer-info: the dynamically-described form. */
  fields?: AttractionFieldDescriptor[];
}

export interface AttractionBookingStep {
  kind: AttractionStepKind;
  label: string;
  order: number;
  required: boolean;
  completed: boolean;
  answer?: unknown;
  payload?: AttractionStepPayload;
}

export interface AttractionCartPricing {
  lines: { productId: string; quantity: number; retailPrice: number }[];
  /** Bridgify's authoritative TOTAL line. Never re-sum the lines yourself. */
  total: number | null;
  extrasTotal: number;
  shipmentFee: number;
  discountAmount: number | null;
  couponCode: string | null;
  /** The amount actually charged (total + extras + delivery − coupon). */
  payableTotal: number | null;
  currency: string;
}

export interface AttractionCartData {
  cartId: string;
  /** Bridgify's ~20-digit customer-facing booking reference. */
  bookingReference: string;
  product: {
    uuid: string;
    title: string;
    image?: string;
    city?: string;
    address?: string;
    slug?: string;
    citySlug?: string;
    countrySlug?: string;
    availabilityType: AttractionAvailabilityType | null;
    durationLabel: string | null;
  };
  steps: AttractionBookingStep[];
  pricing: AttractionCartPricing;
  contactEmail: string | null;
  contactName: string | null;
  status: string;
  sessionEnd: string | null;
  cartExpirationLink: string | null;
  orderUuid: string | null;
}

/** `getSteps` / `submitStep` add the progress cursor and the reset warning. */
export interface AttractionStepsData extends AttractionCartData {
  currentIndex: number;
  totalSteps: number;
  /**
   * Steps whose answers were WIPED by the submission just made. The flow is
   * destructive and ordered — changing the date clears the time, tickets,
   * extras and traveller details — and the customer must be told.
   */
  resetSteps?: AttractionStepKind[];
}

/* ──────────────────────────── orders / cancel ─────────────────────────── */

export type AttractionOrderStatus =
  | 'CONFIRMED'
  | 'CONFIRMED_PENDING'
  | 'AWAITING_SUPPLIER'
  | 'FAILED'
  | 'CANCELLED';

/** `SUPPLIER_DIRECT` = the operator emails tickets itself; we will never have
 *  a link. The UI must not look broken in that case. */
export type AttractionVoucherStatus = 'AVAILABLE' | 'PENDING' | 'SUPPLIER_DIRECT' | 'NONE';

export interface AttractionOrderRecord {
  reservationId: string;
  bookingReference: string;
  product: {
    uuid: string;
    title: string;
    image?: string;
    city?: string;
    address?: string;
    durationLabel: string | null;
  };
  travelDate: string | null;
  travelTime: string | null;
  language: string | null;
  pickup: unknown;
  lines: { productId: string; quantity: number; retailPrice: number }[];
  paxes: Record<string, number>;
  extras: unknown;
  totalPaid: number;
  currency: string;
  discountAmount: number | null;
  couponCode: string | null;
  contactEmail?: string;
  contactName?: string;
  status: AttractionOrderStatus;
  vouchers: string[];
  voucherStatus: AttractionVoucherStatus;
  confirmationTime: string | null;
  cancelledAt: string | null;
  createdAt?: string;
}

export interface AttractionCancellationTerms {
  cancellationAllowed: boolean;
  /** Display word for word. */
  policy: string | null;
  refundPercentage: number | null;
  refundAmount: number | null;
  /**
   * The supplier has not decided the refund. Show "refund amount to be
   * confirmed" — NOT 0% and NOT 100%.
   */
  refundUndecided: boolean;
  currency: string | null;
  travelDate: string | null;
  paxes: Record<string, number>;
  /** Why the cancel button is disabled, when it is. */
  reason?: string;
}

export interface AttractionBookingStatus {
  status: string;
  orderCreated: boolean;
  order: AttractionOrderRecord | null;
}

/* ──────────────────────────────── client ──────────────────────────────── */

export class AttractionsClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    // No throw on a missing URL: this module exports a singleton, so a
    // constructor throw would break every consumer that merely imports the
    // barrel. `BaseApiClient.request` fails lazily with a clear message.
    super(
      baseUrl ??
        (typeof window === 'undefined'
          ? (process.env.ATTRACTIONS_SERVICE_URL ?? process.env.NEXT_PUBLIC_ATTRACTIONS_API_URL)
          : process.env.NEXT_PUBLIC_ATTRACTIONS_API_URL),
    );
  }

  /* catalogue */

  async searchAttractions(params: SearchAttractionsParams = {}): Promise<AttractionListResponse> {
    return this.get<AttractionListResponse>('/attractions', params as Record<string, unknown>);
  }

  async getCities(): Promise<AttractionCityRecord[]> {
    const res = await this.get<{ message: string; data: AttractionCityRecord[] }>('/attractions/cities');
    return res.data;
  }

  async getByUuid(uuid: string): Promise<AttractionRecord> {
    const res = await this.get<{ message: string; data: AttractionRecord }>(
      `/attractions/${encodeURIComponent(uuid)}`,
    );
    return res.data;
  }

  async getBySlug(countrySlug: string, citySlug: string, slug: string): Promise<AttractionRecord> {
    const res = await this.get<{ message: string; data: AttractionRecord }>(
      `/attractions/by-slug/${encodeURIComponent(countrySlug)}/${encodeURIComponent(citySlug)}/${encodeURIComponent(slug)}`,
    );
    return res.data;
  }

  /** The slug alone is unique; the path segments are SEO breadcrumbs. */
  async getBySlugOnly(slug: string): Promise<AttractionRecord> {
    const res = await this.get<{ message: string; data: AttractionRecord }>(
      `/attractions/by-slug-only/${encodeURIComponent(slug)}`,
    );
    return res.data;
  }

  /** Browsing calendar only — see `AttractionAvailability.authoritative`. */
  async getAvailabilityPreview(
    uuid: string,
    params: { dateFrom?: string; dateTo?: string } = {},
  ): Promise<AttractionAvailability | null> {
    const res = await this.get<{ message: string; data: AttractionAvailability | null }>(
      `/attractions/${encodeURIComponent(uuid)}/availability`,
      params as Record<string, unknown>,
    );
    return res.data;
  }

  async autocomplete(q: string, limit?: number): Promise<unknown> {
    const res = await this.get<{ message: string; data: unknown }>('/attractions/autocomplete', {
      q,
      ...(limit ? { limit } : {}),
    });
    return res.data;
  }

  /* cart + step engine */

  /** Runs Bridgify steps 4→6: create cart, add the product, ask for the steps. */
  async prepareCart(body: {
    productUuid: string;
    memberId?: string;
    attribution?: Record<string, string>;
  }): Promise<AttractionCartData> {
    const res = await this.post<{ message: string; data: AttractionCartData }>('/cart/prepare', body);
    return res.data;
  }

  async getCart(cartId: string): Promise<AttractionCartData> {
    const res = await this.get<{ message: string; data: AttractionCartData }>(
      `/cart/${encodeURIComponent(cartId)}`,
    );
    return res.data;
  }

  /** The step queue with the CURRENT step's options hydrated. */
  async getSteps(cartId: string): Promise<AttractionStepsData> {
    const res = await this.get<{ message: string; data: AttractionStepsData }>(
      `/cart/${encodeURIComponent(cartId)}/steps`,
    );
    return res.data;
  }

  /**
   * Submit one step. The answer shape depends on `kind` — the service narrows
   * and validates it, and Bridgify is the final authority.
   */
  async submitStep(
    cartId: string,
    kind: AttractionStepKind,
    answer: Record<string, unknown>,
  ): Promise<AttractionStepsData> {
    const res = await this.post<{ message: string; data: AttractionStepsData }>(
      `/cart/${encodeURIComponent(cartId)}/steps/${kind}`,
      answer,
    );
    return res.data;
  }

  /** Must be applied BEFORE the customer-info step. */
  async applyCoupon(cartId: string, couponCode: string, property?: string): Promise<AttractionCartData> {
    const res = await this.post<{ message: string; data: AttractionCartData }>(
      `/cart/${encodeURIComponent(cartId)}/coupon`,
      { couponCode, ...(property ? { property } : {}) },
    );
    return res.data;
  }

  /** Records the amount and campaign attribution. No money moves. */
  async saveCart(cartId: string): Promise<AttractionCartData> {
    const res = await this.post<{ message: string; data: AttractionCartData }>(
      `/cart/${encodeURIComponent(cartId)}/save`,
      {},
    );
    return res.data;
  }

  /* payment */

  async initiatePayment(
    cartId: string,
  ): Promise<{ clientSecret: string; amount: number; currency: string }> {
    const res = await this.post<{
      message: string;
      data: { clientSecret: string; amount: number; currency: string };
    }>(`/cart/${encodeURIComponent(cartId)}/initiate-payment`, {});
    return res.data;
  }

  /** Poll after `confirmPayment` — the webhook is authoritative but can lag. */
  async getBookingStatus(cartId: string): Promise<AttractionBookingStatus> {
    const res = await this.get<{ message: string; data: AttractionBookingStatus }>(
      `/cart/${encodeURIComponent(cartId)}/booking-status`,
    );
    return res.data;
  }

  /* orders */

  async getOrder(reference: string): Promise<AttractionOrderRecord> {
    const res = await this.get<{ message: string; data: AttractionOrderRecord }>(
      `/orders/${encodeURIComponent(reference)}`,
    );
    return res.data;
  }

  async listOrders(): Promise<AttractionOrderRecord[]> {
    const res = await this.get<{ message: string; data: AttractionOrderRecord[] }>('/orders');
    return res.data;
  }

  /** ALWAYS call this before enabling a cancel button. */
  async getCancellationTerms(reference: string): Promise<AttractionCancellationTerms> {
    const res = await this.get<{ message: string; data: AttractionCancellationTerms }>(
      `/orders/${encodeURIComponent(reference)}/cancellation`,
    );
    return res.data;
  }

  async cancelOrder(
    reference: string,
  ): Promise<{ order: AttractionOrderRecord; result: Record<string, unknown> }> {
    const res = await this.post<{
      message: string;
      data: { order: AttractionOrderRecord; result: Record<string, unknown> };
    }>(`/orders/${encodeURIComponent(reference)}/cancellation`, {});
    return res.data;
  }
}

export const attractionsClient = new AttractionsClient();
